import { getResources } from './../../../app/@store/hybrid-core/hybrid-core.selectors';
import { Store } from '@ngrx/store';
import { ContractManagementApiService } from './../../../app/@shared/services/contractmanagementapi.service';
import { globalFailure } from './../../../app/@store/common.actions';
import { catchError, concatMap, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { Injectable } from '@angular/core';

import * as HybridCoreActions from './hybrid-core.actions';
import moment from '@js/moment-lite';
import { ServiceHybridCoresResponse } from './../../../app/@shared/models/hybridcore_api/serviceHybridCoresResponse';
import { ServiceHybridCoreResponse } from './../../../app/@shared/models/hybridcore_api/serviceHybridCoreResponse';
import { ServiceHybridCoreResourcesResponse } from './../../../app/@shared/models/hybridcore_api/serviceHybridCoreResourcesResponse';
import { ServiceHybridCoreAssetResponse } from './../../../app/@shared/models/hybridcore_api/serviceHybridCoreAssetResponse';

@Injectable()
export class HybridCoreEffects {
  constructor(private readonly actions$: Actions, private readonly apiPublic: ContractManagementApiService, private readonly store: Store) { }

  loadHybridCores$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HybridCoreActions.loadHybridCores),
      concatMap(action =>
        this.apiPublic.get<ServiceHybridCoresResponse>('/hybrid-core/v2/hybrid-cores').pipe(
          filter(result => result?.data !== undefined),
          map((result) =>
            HybridCoreActions.loadHybridCoresSuccess({
              hybridCores: result.data!.filter(elem => elem.id !== 'ac82' && elem.id !== 'ba09')
            })
          ),
          catchError(error => of(HybridCoreActions.loadHybridCoresFailure({ error }), globalFailure({ error })))
        )
      )
    )
  );

  loadHybridCoreDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HybridCoreActions.loadHybridCoreDetails),
      concatMap(action =>
        this.apiPublic.get<ServiceHybridCoreResponse>(`/hybrid-core/v2/hybrid-cores/${action.hybridCoreId}`).pipe(
          map((result) =>
            HybridCoreActions.loadHybridCoreDetailsSuccess({
              hybridCoreId: action.hybridCoreId,
              data: result.data || {}
            })
          ),
          catchError(error => of(HybridCoreActions.loadHybridCoreDetailsFailure({ hybridCoreId: action.hybridCoreId, error }), globalFailure({ error })))
        )
      )
    )
  );

  loadResources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HybridCoreActions.loadResources),
      mergeMap(action => of(action).pipe(
        withLatestFrom(
          this.store.select(getResources({ hybridCoreId: action.hybridCoreId }))
        )
      )),
      concatMap(([action, resources]) => {
        if (resources?.next_measured_at) {
          const nextMeasure = moment(resources.next_measured_at);
          const diff = nextMeasure.diff(moment(), 'seconds');

          if (diff >= -10) {
            return EMPTY;
          }
        }


        return this.apiPublic.get<ServiceHybridCoreResourcesResponse>(`/hybrid-core/v2/hybrid-cores/${action.hybridCoreId}/resources`).pipe(
          map((result) =>
            HybridCoreActions.loadResourcesSuccess({
              resources: result.data || {},
              hybridCoreId: action.hybridCoreId
            })
          ),
          catchError(error => of(HybridCoreActions.loadResourcesFailure({ hybridCoreId: action.hybridCoreId, error }), globalFailure({ error })))
        );
      })
    )
  );

  loadAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HybridCoreActions.loadAssets),
      mergeMap(action => of(action).pipe(withLatestFrom(this.store.select(getResources({ hybridCoreId: action.hybridCoreId }))))),
      concatMap(([action, assets]) => {
        if (assets?.next_measured_at) {
          const nextMeasure = moment(assets.next_measured_at);
          const diff = nextMeasure.diff(moment(), 'seconds');

          if (diff >= -10) {
            return EMPTY;
          }
        }

        return this.apiPublic.get<ServiceHybridCoreAssetResponse>(`/hybrid-core/v2/hybrid-cores/${action.hybridCoreId}/assets`).pipe(
          map((result) =>
            HybridCoreActions.loadAssetsSuccess({
              assets: result.data || {},
              hybridCoreId: action.hybridCoreId
            })
          ),
          catchError(error => of(HybridCoreActions.loadAssetsFailure({ hybridCoreId: action.hybridCoreId, error }), globalFailure({ error })))
        );
      })
    )
  );
}
